import { useState } from "react";
import { DbBusinessClient, DateFields } from "../client/models";
import { DbBusinessService } from "../category-service/models";
import { DbBusinessCalendar } from "../calendar/models";
import { DbBusinessMembership, DbMembershipType } from "../membership/models";
import { DbBusinessPaymentType } from "../business/models";
import type { DbInventoryItem } from "../inventory/models";

export enum WalkInStep {
  SELECTION = 1,
  BILLING = 2,
}

export interface WalkInService {
  id: string | null;
  service: DbBusinessService | null;
  calendar: DbBusinessCalendar | null;
  price: number;
  selectedMembership: DbBusinessMembership | null;
  // When true, the row represents an existing booking/product and should be read-only in UI
  isLocked?: boolean;
  // When true, this preloaded row is covered fully by membership (for info only)
  coveredByMembership?: boolean;
  // Amount covered by membership for this row (for display / totals)
  membershipCoveredAmount?: number;
}

export interface WalkInProduct {
  id: string | null;
  product: DbInventoryItem | null;
  quantity: number;
  price: number;
  // When true, the row represents an existing booking/product and should be read-only in UI
  isLocked?: boolean;
}

export function useWalkInState() {
  const [selectedClient, setSelectedClient] = useState<DbBusinessClient | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<DateFields | null>(null);
  const [services, setServices] = useState<WalkInService[]>([]);
  const [products, setProducts] = useState<WalkInProduct[]>([]);
  const [currentStep, setCurrentStep] = useState<WalkInStep>(
    WalkInStep.SELECTION,
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<DbBusinessPaymentType>(DbBusinessPaymentType.Cash);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedServiceForMembership, setSelectedServiceForMembership] =
    useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [additionalNote, setAdditionalNote] = useState<string>("");

  const resetState = () => {
    setSelectedClient(null);
    setSelectedDate(null);
    setServices([]);
    setProducts([]);
    setCurrentStep(WalkInStep.SELECTION);
    setShowMembershipModal(false);
    setSelectedServiceForMembership(null);
    setSelectedPaymentMethod(DbBusinessPaymentType.Cash);
    setPaymentAmount(0);
    setError(null);
    setAdditionalNote("");
  };

  return {
    selectedClient,
    setSelectedClient,
    selectedDate,
    setSelectedDate,
    services,
    setServices,
    products,
    setProducts,
    currentStep,
    setCurrentStep,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentAmount,
    setPaymentAmount,
    showMembershipModal,
    setShowMembershipModal,
    selectedServiceForMembership,
    setSelectedServiceForMembership,
    isLoading,
    setIsLoading,
    error,
    setError,
    additionalNote,
    setAdditionalNote,
    resetState,
  };
}

export function calculateSubtotal(
  services: WalkInService[],
  products: WalkInProduct[],
): number {
  const servicesTotal = services.reduce(
    (sum, service) => sum + (service.price || 0),
    0,
  );
  const productsTotal = products.reduce(
    (sum, product) => sum + product.quantity * product.price,
    0,
  );
  return servicesTotal + productsTotal;
}

export function calculateTax(products: WalkInProduct[]): number {
  const productsTotal = products.reduce(
    (sum, product) => sum + product.quantity * product.price,
    0,
  );
  return productsTotal * 0;
}

export function calculateServiceMembershipDiscount(
  service: WalkInService,
): number {
  if (!service.selectedMembership) return 0;

  if (service.selectedMembership.type === DbMembershipType.price) {
    const remainingBenefitAmount =
      (service.selectedMembership.benefit_amount || 0) -
      (service.selectedMembership.used_benefit_amount || 0);
    return Math.min(remainingBenefitAmount, service.price);
  } else if (service.selectedMembership.type === DbMembershipType.service) {
    const membershipService = service.selectedMembership.services?.find(
      (ms) => ms.service_id === service.service?.id,
    );
    if (
      membershipService &&
      membershipService.total_count - (membershipService.used_count || 0) >= 1
    ) {
      return service.price;
    }
  }
  return 0;
}

export function calculateTotalMembershipDiscount(
  services: WalkInService[],
): number {
  return services.reduce((total, service) => {
    const dynamicDiscount = calculateServiceMembershipDiscount(service);
    const covered = service.coveredByMembership
      ? service.membershipCoveredAmount || 0
      : 0;
    return total + dynamicDiscount + covered;
  }, 0);
}

export function calculateTotal(
  services: WalkInService[],
  products: WalkInProduct[],
): number {
  const subtotal = calculateSubtotal(services, products);
  const tax = calculateTax(products);
  const membershipDiscount = calculateTotalMembershipDiscount(services);
  return subtotal + tax - membershipDiscount;
}

export function calculateRemainingAmount(
  services: WalkInService[],
  products: WalkInProduct[],
  redeemedDiscount: number,
  externalPrepaidAmount: number,
  paymentAmount: number,
): number {
  const total = calculateTotal(services, products);
  const totalToPay = Math.max(0, total - redeemedDiscount);
  return Math.max(0, totalToPay - externalPrepaidAmount - paymentAmount);
}
